import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ArrowLeft, Send, RefreshCw } from 'lucide-react'
import styles from './CSMWishComposer.module.css'
import AuthContext from '../../store/auth-context'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import { smPost } from './smApi'
import { prettifyOccasionType, emojiForIcon } from './smIcons'
import { SM_TEMPLATES } from './smMockData'

const SM_GET_CUSTOMER_PROFILE = 'sm_get_customer_profile'
const SM_SEND_WISH = 'sm_send_wish'

const GIFT_OPTIONS = [
    { key: 'coupon', label: 'Flat ₹500 Coupon', line: "As a small token of appreciation, here's a flat ₹500 coupon valid on your next visit." },
    { key: 'spa', label: 'Free Hair Spa', line: 'Enjoy a complimentary hair spa on us this month.' },
    { key: 'none', label: 'No Offer', line: '' },
]

const fillTemplate = (body, name, occasion) => {
    const firstName = (name || '').split(/[\s&]+/)[0]
    return body
        .replaceAll('{{firstName}}', firstName)
        .replaceAll('{{yearsAsCustomer}}', '')
        .replaceAll('{{yearsTogether}}', String(occasion?.years_together ?? ''))
}

const CSMWishComposer = () => {
    const { customerIdentifier, occasionId } = useParams()
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [profile, setProfile] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadErrorMessage, setLoadErrorMessage] = useState(null)
    const [sending, setSending] = useState(false)

    const loadProfile = useCallback(async () => {
        setLoading(true)
        setLoadErrorMessage(null)
        const res = await smPost(authCtx, activeStore, SM_GET_CUSTOMER_PROFILE, {
            client_store_relation_identifier: customerIdentifier,
        })
        if (res?.status === 'success' && res?.response) {
            setProfile(res.response)
        } else {
            setLoadErrorMessage(res?.message || 'Customer not found.')
        }
        setLoading(false)
    }, [authCtx, activeStore, customerIdentifier])

    useEffect(() => {
        if (activeStore?.storeIdentifier && customerIdentifier) loadProfile()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, customerIdentifier])

    const occasion = profile?.occasions?.find((o) => String(o.occasion_id) === String(occasionId))

    // Template list is still local/mock — sm_get_templates isn't wired yet,
    // so these only pre-fill the greeting text below. template_id is sent
    // as null until real template IDs exist; only final_greeting_text (what
    // was actually typed/edited) is stored server-side either way.
    const occasionTemplates = useMemo(
        () => (occasion ? SM_TEMPLATES.filter((t) => t.occasion === occasion.occasion_type) : []),
        [occasion]
    )

    const [templateId, setTemplateId] = useState('')
    const [greeting, setGreeting] = useState('')
    const [gift, setGift] = useState('coupon')

    useEffect(() => {
        if (!occasion || occasionTemplates.length === 0) return
        setTemplateId((prev) => prev || occasionTemplates[0].identifier)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [occasion, occasionTemplates])

    useEffect(() => {
        if (!occasion || !profile) return
        const tpl = SM_TEMPLATES.find((t) => t.identifier === templateId) || occasionTemplates[0]
        if (tpl) setGreeting(fillTemplate(tpl.body, profile.name, occasion))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, occasion, profile])

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading…</span>
            </div>
        )
    }

    if (loadErrorMessage || !profile || !occasion) {
        return (
            <div className={styles.page}>
                <Link to="/special-moments/inbox" className={styles.backLink}>
                    <ArrowLeft size={14} /> Back to Celebration Inbox
                </Link>
                <div className={styles.notFound}>
                    {loadErrorMessage || (profile ? 'This occasion could not be found for this customer.' : 'Customer not found.')}
                </div>
            </div>
        )
    }

    const selectedGift = GIFT_OPTIONS.find((g) => g.key === gift)
    const previewText = [greeting, selectedGift?.line].filter(Boolean).join('\n\n')

    const handleSend = async () => {
        setSending(true)
        const res = await smPost(authCtx, activeStore, SM_SEND_WISH, {
            occasion_id: occasion.occasion_id,
            template_id: null,
            final_greeting_text: previewText,
            gift_offer_key: gift,
            channel: 'whatsapp',
            sent_by: authCtx?.authIdentifier,
        })
        setSending(false)

        if (res?.status === 'success' && res?.response) {
            toast.success(`Wish sent to ${profile.name} 🎉`)
            navigate('/special-moments/inbox')
        } else {
            toast.error(res?.message || "Couldn't send this wish right now.")
        }
    }

    return (
        <div className={styles.page}>
            <Link to={`/special-moments/customer/${customerIdentifier}`} className={styles.backLink}>
                <ArrowLeft size={14} /> Back to {profile.name}
            </Link>

            <h1 className={styles.pageTitle}>{emojiForIcon(occasion.icon)} Wish {profile.name}</h1>

            <div className={styles.sectionLabel}>Template</div>
            <select
                className={styles.templateSelect}
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
            >
                {occasionTemplates.map((t) => (
                    <option key={t.identifier} value={t.identifier}>{t.name}</option>
                ))}
                {occasionTemplates.length === 0 && (
                    <option value="">{prettifyOccasionType(occasion.occasion_type)} (default)</option>
                )}
            </select>

            <div className={styles.sectionLabel}>Greeting</div>
            <textarea
                className={styles.greetingInput}
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                rows={7}
            />

            <div className={styles.sectionLabel}>Gift</div>
            <div className={styles.giftOptions}>
                {GIFT_OPTIONS.map((g) => (
                    <label key={g.key} className={styles.giftOption}>
                        <input
                            type="checkbox"
                            checked={gift === g.key}
                            onChange={() => setGift(g.key)}
                        />
                        {g.label}
                    </label>
                ))}
            </div>

            <div className={styles.sectionLabel}>Preview</div>
            <div className={styles.previewBox}>
                {previewText.split('\n').map((line, idx) => (
                    <p key={idx} className={styles.previewLine}>{line || ' '}</p>
                ))}
            </div>

            <button type="button" className={styles.sendBtn} onClick={handleSend} disabled={sending}>
                {sending ? <RefreshCw size={15} className={styles.spinner} /> : <Send size={15} />}
                {' '}Send
            </button>
        </div>
    )
}

export default CSMWishComposer
